import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { marketplaceKeys } from "@/lib/queries/marketplace-query-keys";
import { activeTasksQueryOptions, taskProgressKeys } from "@/lib/queries/task-progress.queries";
import { personaOpportunityHubKeys } from "@/lib/queries/persona-opportunity-hub.queries";
import {
  PERSONA_GENERATION_POLL_MS,
  personaOpportunitySetKeys,
} from "@/lib/queries/persona-opportunity-sets.queries";
import { ApiError } from "@/lib/api-client";
import { cancelTask, dismissTask } from "@/features/task-progress/api/task-progress.api";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  fetchPersonaOpportunitySetDetail,
  type PersonaOpportunitySetListPage,
} from "@/features/persona-opportunities/api/persona-opportunity-sets.api";
import {
  markPersonaSetGenerationCancelled,
  unmarkPersonaSetGenerationCancelled,
} from "@/features/persona-opportunities/lib/persona-set-generation-task";
import { suppressPersonaOpportunitySetId, unsuppressPersonaOpportunitySetId } from "@/features/persona-opportunities/lib/persona-opportunity-set-suppression";

const softDeleteWatches = new Map<string, AbortController>();

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

async function setIsSoftDeleted(setId: string): Promise<boolean> {
  try {
    await fetchPersonaOpportunitySetDetail(setId);
    return false;
  } catch (err) {
    return err instanceof ApiError && err.status === 404;
  }
}

/**
 * Cancel only flags the task. The worker soft-deletes the set later, and a
 * live set still belongs in the hub totals until that write lands.
 */
export function refreshHubAfterPersonaSetSoftDelete(
  queryClient: QueryClient,
  tenantId: string | null | undefined,
  setId: string,
): void {
  softDeleteWatches.get(setId)?.abort();
  const controller = new AbortController();
  softDeleteWatches.set(setId, controller);
  void (async () => {
    while (!controller.signal.aborted) {
      await delay(PERSONA_GENERATION_POLL_MS, controller.signal);
      if (controller.signal.aborted) return;
      if (!(await setIsSoftDeleted(setId))) continue;
      if (controller.signal.aborted) return;
      void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
      if (tenantId) {
        void queryClient.invalidateQueries({ queryKey: marketplaceKeys.orders(tenantId) });
      }
      softDeleteWatches.delete(setId);
      return;
    }
  })();
}

/** Test helper. Production watches end when the set 404s. */
export function stopHubSoftDeleteWatches(): void {
  for (const controller of softDeleteWatches.values()) controller.abort();
  softDeleteWatches.clear();
}

/** Prefix matching every tenant's persona opportunity-sets list cache. */
const SETS_LIST_PREFIX = [...personaOpportunitySetKeys.all, "list"] as const;

/**
 * Cancels in-flight persona opportunity-set generation for a setId.
 * Same cancel → dismiss sequence as the task sidebar Stop button, but invalidates
 * `personaOpportunitySetKeys` list cache (not legacy opportunity sets).
 *
 * Returns true when a cancellable task was found and cancellation was requested.
 * `onCancelled` runs only after cancel + dismiss succeed.
 */
export function useStopPersonaOpportunityGeneration(): (
  setId: string,
  onCancelled?: (correlationId: string) => void,
) => boolean {
  const userEmail = useAuth((s) => s.user?.email);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  const { data: activeTasks = [] } = useQuery(activeTasksQueryOptions(userEmail ?? undefined));

  const cancelMutation = useMutation({
    mutationFn: async ({ correlationId }: { correlationId: string; setId: string }) => {
      await cancelTask(correlationId);
      await dismissTask(correlationId).catch(() => {});
    },
    onMutate: ({ setId, correlationId }) => {
      // Dismissing the task must not look like a finished generation. Hub
      // counts refresh only after the worker soft-deletes the set.
      markPersonaSetGenerationCancelled(correlationId);
      // Suppress before list invalidations (delete / poll) can refetch the still-
      // GENERATING row and flash the card back — same race as legacy opportunity sets.
      suppressPersonaOpportunitySetId(setId);
      queryClient.setQueriesData<PersonaOpportunitySetListPage>(
        { queryKey: SETS_LIST_PREFIX },
        (prev) => {
          if (!prev || !Array.isArray(prev.items)) return prev;
          return { ...prev, items: prev.items.filter((set) => set.id !== setId) };
        },
      );
      // Drop the workspace detail cache so a cancelled GENERATING set cannot keep
      // painting the workspace from stale data after the list row is removed.
      queryClient.removeQueries({
        queryKey: personaOpportunitySetKeys.details(),
        predicate: (query) => query.queryKey[3] === setId,
      });
    },
    onSuccess: (_data, { setId }) => {
      // Do NOT invalidate the sets list here — refetch would race the worker
      // soft-delete and restore the GENERATING card (see suppress helper).
      refreshHubAfterPersonaSetSoftDelete(queryClient, activeTenantId, setId);
      if (userEmail) {
        void queryClient.invalidateQueries({ queryKey: taskProgressKeys.active(userEmail) });
      }
    },
    onError: (_err, { setId, correlationId }) => {
      unmarkPersonaSetGenerationCancelled(correlationId);
      softDeleteWatches.get(setId)?.abort();
      softDeleteWatches.delete(setId);
      // Cancel failed — allow the set back into the list.
      unsuppressPersonaOpportunitySetId(setId);
      if (userEmail) {
        void queryClient.invalidateQueries({ queryKey: taskProgressKeys.active(userEmail) });
      }
      void queryClient.invalidateQueries({ queryKey: SETS_LIST_PREFIX });
    },
  });

  return useCallback(
    (setId: string, onCancelled?: (correlationId: string) => void) => {
      const task = activeTasks.find(
        (t) =>
          t.cancellable === true &&
          t.navigation?.params?.setId === setId &&
          (t.type === "generate-persona-opportunity-set" ||
            (t.type === "generate-opportunity-set" &&
              t.navigation?.page === "persona-opportunity-sets")),
      );
      if (!task) return false;
      cancelMutation.mutate(
        { correlationId: task.correlationId, setId },
        { onSuccess: () => onCancelled?.(task.correlationId) },
      );
      return true;
    },
    [activeTasks, cancelMutation],
  );
}
